import { Alpha3Code } from 'i18n-iso-countries'

import { PBPListResponse, PBPResponse } from '../types/pbpResponses.types'

import { QueryParam } from '@percent/partner-dashboard/common/queryParams/queryParams.types'
import { CurrencyCode, Money } from '@percent/utility'

export enum CurrentStateLabel {
  VERIFIED = 'Verified',
  REJECTED = 'Rejected',
  FINALISTS = 'Finalists',
  DISBURSEMENTS = 'Disbursements',
  DONATION_PAID = 'Donation Paid'
}

export enum CurrentStateDB {
  ALL = 'STATE_ALL',
  VERIFIED = 'STATE_NOMINATED',
  REJECTED = 'STATE_REJECTED',
  FINALISTS = 'FINALISTS'
}

export enum CandidateRejectionReasonCode {
  NON_ELIGIBLE = 'not_eligible',
  INCORRECT_DOCUMENTATION = 'incorrect_documentation',
  NOT_ATTRIBUTABLE_TO_REGISTRY = 'not_attributable_to_registry',
  OTHER = 'other'
}

export enum EntryType {
  NOMINATION = 'nomination',
  APPLICATION = 'application',
  INVITATION = 'invitation'
}

export type Program = {
  id: string
  name: string
  opensAt: string
  closesAt?: string
  nominationsEnabled: boolean
  partnerId: string
  validationSubmissionConfigurationId: string | null
  entryType: EntryType
}

export type ProgramsResponse = PBPResponse<Program[]>

export type DonationStatus = 'processing' | 'paid'

/**
 * The machine-readable counterpart to `rejectionReason`, so the copy lives here rather than on the
 * backend. Discriminated on `code`: a reason added later is a new variant with its own params.
 * Absent for every rejection the backend has not classified, which is all of them but this one.
 */
/**
 * Which verification check rejected the candidate. Closed on the backend, which reports an
 * unrecognised check as no detail at all rather than publishing an internal identifier.
 */
export type FailedCheck = 'compliance' | 'eligibility' | 'validation_request' | 'agent_verification'

export type RejectionReasonDetail =
  | { code: 'task_failed'; taskName: string }
  | { code: 'task_condition_matched'; taskName: string }
  | { code: 'check_failed'; check: FailedCheck }
  /**
   * The name of the scheduled event that fired, authored per program on a `scheduled_event` form
   * question. Rendered as authored: the set is open, so the name is the only thing that says which
   * of a program's events this was. Normalised and length-bounded before the backend publishes it.
   */
  | { code: 'scheduled_event'; name: string }
  /**
   * A person rejected the candidate rather than a rule, and `reason` is the text they wrote. The one
   * variant that carries prose: free text in whatever language its author used, never translated,
   * and the same string `rejectionReason` returns. Absent when they wrote nothing, where the code
   * alone still says a person decided it.
   */
  | { code: 'admin_rejection'; reason?: string }
  /**
   * Refs of the conditions on the candidate's accumulated state that routed it into the rejected
   * state. An internal path, not copy — `organisation.country`, or `events.EVENT_X.responses.score`
   * whose key the program author chose. The set is open, so the ones we have no copy for are
   * dropped rather than shown.
   */
  | { code: 'condition_matched'; conditions: string[] }
  | { code: 'duplicate' }

export type DisbursedBy = 'goodstack' | 'other'

export type Candidate = {
  id: string
  createdAt: string
  organisation: {
    name: string
    countryCode: string
  }
  reviewers: CandidateReviewer[]
  currentState: CurrentStateLabel
  rejectionReason: string | null | undefined
  rejectionReasonDetail?: RejectionReasonDetail
  tasks: TaskInGetCandidates[]
  award?: {
    amount: number
    currency: CurrencyCode
  }
  paymentDetails?: {
    payment?: {
      amount: number
      currency: CurrencyCode
    }
    status: DonationStatus
    paidAt?: Date
  }
  scores?: CandidateAggregateScore[]
}

export type TaskInGetCandidates = {
  id: string
  assignee?: {
    accountId: string
    email: string
  }
  status: TaskStatus
  deadline?: string
  templateId: string
}

export type CandidatesResponse = PBPListResponse<Candidate>

type ActivitySubtag = {
  id: string
  name: string
  description: string
  createdAt: string
  updatedAt: string | null
  deprecatedAt: string | null
  tag: {
    id: string
    name: string
    description: string | null
    createdAt: string
    updatedAt: null
  }
}

type EligibilityResult = {
  eligibilityStatus: 'pass' | 'fail' | null
  confirmedActivitySubTags: ActivitySubtag[] | null
  rejectedActivitySubTags: ActivitySubtag[] | null
}

export enum OrganisationVerificationStatus {
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  VERIFIED = 'VERIFIED'
}

export type CandidateDetails = CandidateNominationDetails[]

export type CandidateResponse = {
  id: string
  organisation: {
    name: string
    website?: string
    email?: string
    country: Alpha3Code
    address?: string
    registryId?: string
    registryName?: string
    logo?: string
    eligibilityResult?: EligibilityResult
    verificationStatus: OrganisationVerificationStatus | null
  }
  state: {
    id: string
    label: string
    reason?: string
    rejectionReasonDetail?: RejectionReasonDetail
  }
  award?: {
    amount: number
    currency: CurrencyCode
  }
  paymentDetails?: {
    disbursedBy: DisbursedBy
    payment?: {
      amount: number
      currency: CurrencyCode
    }
    status: DonationStatus
    paidAt?: Date
  }
  organisationContact?: {
    firstName: string
    lastName: string
    email: string
  }
  locale?: string
  details?: CandidateDetails
  scores?: CandidateAggregateScore[]
}

export type CandidateAggregateScore = {
  name: string
  value: number
}

export type CandidateDocument = {
  id: string
  name: string | null
  url: string
  createdAt: string
}

export type UploadCandidateDocumentsParams = {
  candidateId: string
  formData: FormData
  saveToDb?: boolean
}

export type UploadCandidateDocumentsResponse = {
  s3Keys: string[]
}

export type CandidateNominationDetails = {
  questionId: string
  questionKey?: string
  questionType?: QuestionType
  questionLabel: string
  originalLocale: string
  translations: Record<string, string> & {
    'en-US': string
  }
}

export type CandidateNomination = {
  id: string
  nominator: {
    firstName: string
    lastName: string
    email: string
    group?: string
  }
  organisationContact?: Partial<{
    firstName: string
    lastName: string
    email: string
    phone: string
  }>
  details: CandidateNominationDetails[]
  submittedAt: string
}

export type CandidateNominationsResponse = {
  candidateId: string
  nominations: CandidateNomination[]
}

type BaseCandidateFormQuestion = {
  id: string
  key: string
  label: string
  placeholder?: string
  description?: string
  required: boolean
  hidden?: boolean
  order: number
  validationRules?: import('@percent/utility').ValidationRule[]
  instructions?: string
}

export type CandidateFormSelectOption = {
  label: string
  value: string
  conditionalQuestionIds?: string[]
}

export type CandidateFormDecisionKey = 'pass' | 'fail'

export type CandidateFormDecisionOption = CandidateFormSelectOption & {
  key: CandidateFormDecisionKey
}

export type ScoreScale = {
  maxScore: number
  scoreWeight?: number
}

export type MoneySubType = 'award' | 'final_award' | 'operating_budget'

export type CandidateFormQuestion =
  | (BaseCandidateFormQuestion & {
      type:
        | 'text'
        | 'award_amount'
        | 'final_award_amount'
        | 'documents'
        | 'date'
        | 'scheduled_event'
        | 'payment_date'
        | 'email'
        | 'number'
        | 'country_search'
    })
  | (BaseCandidateFormQuestion & { type: 'textarea'; maxLength?: number })
  | (BaseCandidateFormQuestion &
      ScoreScale & {
        type: 'score'
      })
  | (BaseCandidateFormQuestion & {
      type: 'select'
      options: CandidateFormSelectOption[]
      multiple?: boolean
    })
  | (BaseCandidateFormQuestion & {
      type: 'decision'
      options: CandidateFormDecisionOption[]
    })
  | (BaseCandidateFormQuestion & {
      type: 'award_recommendation'
      options: CandidateFormSelectOption[]
    })
  // `money` questions are paired by key prefix: `<prefix>.amount` + `<prefix>.currency`. The amount is
  // captured in minor units. Bounds on the amount half ride on the shared validationRules.
  | (BaseCandidateFormQuestion & {
      type: 'money'
      subType?: MoneySubType
      defaultCurrency?: CurrencyCode
      allowedCurrencies?: CurrencyCode[]
    })

export type CandidateFormPage = {
  id: string
  title: string
  description: string
  order: number
  questions: CandidateFormQuestion[]
}

export type CandidateForm = {
  candidateForm: {
    id: string
    type: string
    displayName: string
    deadline: string | null
    description: string
    pages: CandidateFormPage[]
  }
}

export type CandidateReviewer = {
  accountId: string
  fullName?: string
  email: string
}

export type CandidateFormSubmissionsResponse = CandidateFormSubmission[]

export type QuestionType =
  | 'score'
  | 'text'
  | 'email'
  | 'textarea'
  | 'number'
  | 'decision'
  | 'select'
  | 'award_recommendation'
  | 'award_amount'
  | 'final_award_amount'
  | 'documents'
  | 'date'
  | 'scheduled_event'
  | 'payment_date'
  | 'country_search'
  | 'money'

export type FormAudience = 'organisation' | 'partner'

export const AWARD_QUESTION_TYPES: QuestionType[] = ['award_amount', 'final_award_amount']

export const DATE_QUESTION_TYPES: QuestionType[] = ['date', 'scheduled_event', 'payment_date']

type BaseCandidateFormSubmissionAnswer = {
  pageId: string
  pageTitle: string
  id: string
  questionId: string
  questionKey?: string
  questionLabel: string
  dashboardLabel?: string
  answer: string
  decision?: CandidateFormDecisionKey
  translatedAnswer?: {
    detectedSourceLanguage: string
    localizedAnswers: Record<string, string>
  } | null
  translatedQuestionLabel?: Record<string, string>
}

export type CandidateFormSubmissionAnswer =
  | (BaseCandidateFormSubmissionAnswer &
      ScoreScale & {
        type: 'score'
      })
  | (BaseCandidateFormSubmissionAnswer & {
      type: Exclude<QuestionType, 'score'>
    })

export type CandidateFormSubmission = {
  id: string
  taskId: string
  displayName: string
  audience: FormAudience
  author: {
    accountId: string
    fullName: string | null
    email: string
  } | null
  formAnswers: CandidateFormSubmissionAnswer[]
  createdAt: string
  scoreAggregate?: {
    mean: number
    sum: number
  }
}

export type GetProgramParams = {
  programId: string
}

export type TaskTemplate = {
  id: string
  name: string | null
  copies?: number
}

export type ProgramStatesResponse = PBPResponse<{ states: ProgramState[] }>

export type ProgramStateTableType =
  | 'task'
  | 'task_with_score'
  | 'multiple_tasks'
  | 'rejected'
  | 'status'
  | 'disbursement'
  | null

type ProgramStateDisplayMode = 'hidden' | 'enabled' | 'disabled'

export type ProgramState = {
  id: string
  displayKey: string
  displayMode: ProgramStateDisplayMode
  displayGroup: string
  tableType: ProgramStateTableType
  scoreName?: string | null
  taskTemplates?: TaskTemplate[] | null
}

export type ProgramStateOption = {
  label: string
  key: string
  disabled: boolean
  tableType: ProgramStateTableType
  scoreName?: string | null
}

export type GetCandidatesSortBy = 'submittedAt' | 'paidAt' | 'score' | 'organisationName'
export type GetCandidatesParams = {
  programId: string
  pageSize?: number
  direction?: QueryParam<'ASC' | 'DESC'>
  sortBy?: QueryParam<GetCandidatesSortBy>
  scoreName?: QueryParam<string>
  /** Inclusive bounds on the scoreName score; candidates without that score are excluded */
  scoreMin?: QueryParam<number>
  scoreMax?: QueryParam<number>
  searchTerm?: QueryParam<string>
  currentState?: QueryParam<CurrentStateDB[]>
  assigneeIds?: string[]
  includeUnassigned?: boolean
}

export type GetCandidateParams = {
  candidateId: string
  programId: string
}

export type GetCandidateFormParams = {
  programId: string
  formId: string
  candidateId: string
}

export type GetCandidateCommentsParams = GetCandidateParams & {
  pageSize?: number
  direction?: 'ASC' | 'DESC'
}

type CandidateCommentAuthor = {
  fullName: string | null
  email: string | null
}

export type CandidateComment = {
  id: string
  content: string
  author: CandidateCommentAuthor
  replyCount?: number
  createdAt: string
  updatedAt: string | null
}

export type CreateGrantCandidateCommentParams = {
  programId: string
  candidateId: string
  content: string
  parentId?: string
  visibility: 'public' | 'private'
}

export type GetCandidateCommentRepliesParams = GetCandidateParams & {
  commentId: string
}

export type TaskStatus = 'todo' | 'done' | 'wont_do'

export type GetMyTasksParams = {
  pageSize?: number
  sortBy?: 'due_by_date'
  direction?: 'ASC' | 'DESC'
  programId?: string
  candidateId?: string
  withForm?: boolean
  status?: TaskStatus
}

type TaskAssignment = {
  id: string
  assignedTo: string
  assignedBy: string
  assignedAt: string
}

export type Task = {
  id: string
  status: string
  createdAt: string
  updatedAt: string | null
  completedAt: string | null
  dueByDate: string | null
  name: string
  form: {
    formId?: string
    displayName?: string
  } | null
  assignment: TaskAssignment
  candidate: {
    id: string
    name: string
    countryCode: Alpha3Code
  }
  program: {
    id: string
    name: string
  }
}

export type PostCandidateFormParams = {
  programId: string
  candidateId: string
  taskId: string
  formAnswers: { questionId: string; answer: string | string[] }[]
}

export type TaskAssignee = {
  id: string
  email: string
}

export type UpdateTaskAssigneeParams = {
  taskId: string
  assignedTo: string
}

export type MarkTaskWontDoParams = {
  programId: string
  candidateId: string
  taskId: string
}

export type GetProgramFormQuestionsResponse = {
  taskTemplate: {
    id: string
    name: string
  }
  questions: {
    id: string
    label: string
    key: string
    dashboardLabel?: string
  }[]
}[]

export type GetCandidatesReportParams = {
  programId: string
  questionIds?: string[]
} & (
  | { currentState?: string | string[] }
  | {
      displayGroup?: string
    }
)

export type SubmittedBy = { id: string | null; email: string | null }

export type GetCandidatesReportResponse = {
  id: string
  currentState: string
  organisation: {
    name: string | null
    countryCode: string | null
  }
  rejectionReason?: string
  createdAt: Date
  award?: { currency: CurrencyCode; amount: number }
  paymentDetails?: {
    disbursedBy: string
    payment?: Money
    status: DonationStatus
    paidAt?: Date
  }
  submissions: {
    id: string
    taskTemplateId: string
    submittedBy: SubmittedBy
    submittedAt: Date | null
    answers: { questionId: string; dashboardLabel: string; type: string; value: string }[]
  }[]
}[]

export type SubmitNominationParams = {
  programId: string
  nomination: {
    nominator: {
      firstName: string
      lastName: string
      email: string
    }
    organisationContact: {
      firstName: string
      email: string
    }
  }
  country: string
  organisation:
    | { id: string }
    | {
        name: string
        country: string
        registry: string
        registryId?: string
        website?: string
      }
  locale: string
}

export type CandidateHistoryActor = { type: 'account'; accountId: string } | { type: 'system'; origin: string }

export type CandidateAttributeChange = {
  field: string
  previousValue: unknown
  newValue: unknown
}

export type CandidateHistoryChangeEntry = {
  kind: 'created' | 'field_change' | 'deleted'
  occurredAt: string
  actor: CandidateHistoryActor
  changes: CandidateAttributeChange[]
  mergedFromCandidateId?: string | null
}

export type CandidateEvaluationLeafKind =
  | 'time'
  | 'eventPresence'
  | 'organisation'
  | 'payloadCondition'
  | 'dataEvent'
  | 'field'
  | 'event'

/**
 * `label` is the state machine's own rendering of the comparison, written for engineers. `detail` is
 * the structured version of the same thing, shaped per `kind` — prefer it, and fall back to `label`
 * for the kinds we have no template for.
 */
export type CandidateEvaluationLeaf = {
  kind: CandidateEvaluationLeafKind
  label: string
  detail?: Record<string, unknown>
}

export type CandidateEvaluationNode =
  | { node: 'group'; satisfied: boolean; children: CandidateEvaluationNode[] }
  | { node: 'leaf'; satisfied: boolean; leaf: CandidateEvaluationLeaf }

export type CandidateStateChangeReason =
  | {
      type: 'transition'
      chosen: { evaluation: CandidateEvaluationNode; satisfiedLeaves: number; totalLeaves: number }
    }
  | { type: 'override'; override: 'admin-rejection' }

export type CandidateHistoryStateChangeEntry = {
  kind: 'state_change'
  occurredAt: string
  fromStateId: string | null
  toStateId: string
  triggeringEventType: string
  reason: CandidateStateChangeReason
}

export type CandidateEmailRecipientType = 'org_primary_contact' | 'nominator'

export type CandidateHistoryEmailEntry = {
  kind: 'email_sent'
  occurredAt: string
  actor: CandidateHistoryActor
  template: string
  subject: string | null
  recipientType: CandidateEmailRecipientType
}

export type CandidateHistoryEntry =
  | CandidateHistoryChangeEntry
  | CandidateHistoryStateChangeEntry
  | CandidateHistoryEmailEntry

export type CandidateHistory = {
  candidateId: string
  entries: CandidateHistoryEntry[]
}
